// One route-owned command-agent map. It decides whether a staff screen owns a
// rail dock, a floating agent, its own native agent, or no agent because there is
// no safe primary action there yet. Both App.jsx (narrow floating surface) and
// rail-bearing screens read the same semantic context from this file.

const ROUTES = new Map([
  ['/queue', { owner: 'dock', breakpoint: 1280, surface: 'today', screen: 'Consult queue', world: 'opd' }],
  ['/reception', { owner: 'floating', surface: 'reception', screen: 'Reception', world: 'opd' }],
  // Emergency's rail remains in the layout at every width (it stacks above the
  // board at <=1120px), so its dock is always the single presentation. Mounting
  // a floating twin below an invented breakpoint would duplicate the agent.
  ['/emergency', { owner: 'dock', alwaysDock: true, breakpoint: 0, surface: 'emergency', screen: 'Emergency department board', world: 'emergency' }],
  ['/ward', { owner: 'dock', breakpoint: 1281, surface: 'ward-board', screen: 'Ward board', world: 'ipd' }],
  ['/ward/worklist', { owner: 'dock', breakpoint: 1181, surface: 'ward-worklist', screen: 'Ward worklist', world: 'ipd' }],
  ['/patients', { owner: 'dock', breakpoint: 1280, surface: 'patients', screen: 'Patients', world: 'opd' }],
  ['/reports', { owner: 'dock', breakpoint: 1280, surface: 'reports', screen: 'Reports', world: 'opd' }],
  ['/schedule', { owner: 'dock', breakpoint: 1280, surface: 'schedule', screen: 'Schedule', world: 'opd' }],
  ['/settings', { owner: 'dock', breakpoint: 1280, surface: 'settings', screen: 'Settings', world: 'opd' }],
  ['/tasks', { owner: 'dock', breakpoint: 1181, surface: 'tasks', screen: 'Tasks', world: 'opd' }],
  ['/messaging', { owner: 'floating', surface: 'messaging', screen: 'Messaging', world: 'opd' }],
  // Screens without a page-native write vocabulary still keep the safe global
  // agent visible. Its closed action catalog can navigate/read and must state an
  // explicit refusal for unsupported page work; hiding it made capabilities
  // differ silently by route.
  ['/nurse', { owner: 'floating', surface: 'nurse', screen: 'Nurse station', world: 'ipd' }],
  ['/ward/templates', { owner: 'floating', surface: 'ward-templates', screen: 'Ward templates', world: 'ipd' }],
  ['/ward/patients', { owner: 'floating', surface: 'ward-patients', screen: 'Ward patients', world: 'ipd' }],
  ['/lab', { owner: 'floating', surface: 'lab', screen: 'Laboratory', world: 'opd' }],
  ['/automations', { owner: 'floating', surface: 'automations', screen: 'Automations', world: 'opd' }],
  ['/admin/staff', { owner: 'floating', surface: 'admin-staff', screen: 'Staff administration', world: 'admin' }],
  ['/admin/settings', { owner: 'floating', surface: 'admin-settings', screen: 'Administration settings', world: 'admin' }],
  ['/admin/analytics', { owner: 'floating', surface: 'admin-analytics', screen: 'Administration analytics', world: 'admin' }],
  ['/admin/outbox', { owner: 'floating', surface: 'admin-outbox', screen: 'Notification outbox', world: 'admin' }],
  ['/admin/attendance', { owner: 'floating', surface: 'admin-attendance', screen: 'Attendance', world: 'admin' }],
  ['/kiosk-setup', { owner: 'floating', surface: 'kiosk-setup', screen: 'Kiosk setup', world: 'opd' }],
]);

const NONE = new Set([
  '/ward/bedside',
  // Spaces and patient workspaces already own a purpose-built agent; bedside
  // and token display are device/public surfaces where a staff agent is unsafe.
  '/spaces',
  '/token-display',
]);

function cleanPath(path) {
  const p = String(path || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return p || '/';
}

export function agentRouteForPath(path, user = null) {
  const pathname = cleanPath(path);
  const role = String(user?.role || user || 'staff').toLowerCase();
  if (pathname.startsWith('/consult/')) return { owner: 'native', context: null };
  if (/^\/ward\/bed\/[^/]+$/.test(pathname) || /^\/emergency\/[^/]+$/.test(pathname)) return { owner: 'native', context: null };
  if (pathname.startsWith('/ward/bedside/')) return { owner: 'none', context: null };
  if (pathname === '/spaces') return { owner: 'native', context: null };
  if (NONE.has(pathname)) return { owner: 'none', context: null };
  const spec = ROUTES.get(pathname);
  if (!spec) return { owner: 'none', context: null };
  return { owner: spec.owner, context: { ...spec, role, path: pathname } };
}

export function agentPresentationForPath(path, user, viewportWidth) {
  const route = agentRouteForPath(path, user);
  if (route.owner !== 'dock') return { ...route, presentation: route.owner };
  return { ...route, presentation: Number(viewportWidth) >= route.context.breakpoint ? 'dock' : 'floating' };
}

export const __agentRoutesTest = { ROUTES, NONE, cleanPath };
